function nextIndex(items, firstIndex = 0) {
  if (items.length === 0) return firstIndex;

  return Math.max(...items.map((item) => Number(item.index) || 0)) + 1;
}

function buildDraftItem(index, attributes = {}) {
  return {
    id: null,
    name: "",
    index,
    _destroy: false,
    _key: `${Date.now()}-${Math.random()}-${index}`,
    ...attributes
  };
}

function buildDraftTemplate(index) {
  return {
    id: null,
    index,
    template_id: "",
    template: null,
    _destroy: false,
    _key: `${Date.now()}-${Math.random()}-template-${index}`
  };
}

function itemKey(item) {
  return item.id || item._key;
}

function idsMatch(left, right) {
  return left != null && right != null && left.toString() === right.toString();
}

function OrderedListEditor({
  eyebrow,
  title,
  emptyLabel,
  addLabel,
  items,
  onAdd,
  onUpdate,
  onRemove,
  nameLabel = "Navn",
  descriptionLabel = null,
  minIndex = 0
}) {
  return (
    <section className="workspace-editor-panel">
      <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
        <div>
          <div className="workspace-section__eyebrow">{eyebrow}</div>
          <h3 className="workspace-editor-panel__title">{title}</h3>
        </div>

        <button type="button" className="btn btn-outline-secondary" onClick={onAdd}>
          {addLabel}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="workspace-editor-panel__empty">{emptyLabel}</div>
      ) : (
        <div className="workspace-inline-list">
          {items.map((item) => {
            const key = itemKey(item);
            const descriptionId = `ordered-item-${key}-description`;

            return (
              <div
                key={key}
                className={`workspace-inline-list__row${descriptionLabel ? " workspace-inline-list__row--with-description" : ""}`}
              >
                <div className="workspace-inline-list__index">
                  <label className="form-label fw-semibold">Nr.</label>
                  <input
                    className="form-control"
                    type="number"
                    min={minIndex}
                    value={item.index}
                    onChange={(event) => onUpdate(key, { index: event.target.value })}
                    required
                  />
                </div>

                <div className="workspace-inline-list__name">
                  <label className="form-label fw-semibold">{nameLabel}</label>
                  <input
                    className="form-control"
                    value={item.name}
                    onChange={(event) => onUpdate(key, { name: event.target.value })}
                    required
                  />

                  {descriptionLabel ? (
                    <div className="workspace-inline-list__description">
                      <label className="form-label fw-semibold" htmlFor={descriptionId}>{descriptionLabel}</label>
                      <textarea
                        id={descriptionId}
                        className="form-control"
                        rows="3"
                        value={item.description || ""}
                        onChange={(event) => onUpdate(key, { description: event.target.value })}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="workspace-inline-list__action">
                  <button type="button" className="btn btn-outline-danger" onClick={() => onRemove(key)}>
                    Fjern
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TemplateListEditor({ templates, stepTemplates, onAdd, onUpdate, onRemove }) {
  const hasAvailableTemplate = templates.some((template) => (
    !template.associated_step_id &&
    !stepTemplates.some((stepTemplate) => idsMatch(stepTemplate.template_id, template.id))
  ));

  return (
    <section className="workspace-editor-panel">
      <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
        <div>
          <div className="workspace-section__eyebrow">Skabeloner</div>
          <h3 className="workspace-editor-panel__title">Skabeloner for trinnet</h3>
        </div>

        <button type="button" className="btn btn-outline-primary" onClick={onAdd} disabled={!hasAvailableTemplate}>
          Tilknyt skabelon
        </button>
      </div>

      {stepTemplates.length === 0 ? (
        <div className="workspace-editor-panel__empty">Ingen skabeloner er tilknyttet trinnet endnu.</div>
      ) : (
        <div className="workspace-inline-list">
          {stepTemplates.map((stepTemplate) => {
            const key = itemKey(stepTemplate);
            const availableTemplates = templates.filter((template) => (
              idsMatch(template.id, stepTemplate.template_id) || (
                !template.associated_step_id &&
                !stepTemplates.some((otherStepTemplate) => (
                  itemKey(otherStepTemplate) !== key && idsMatch(otherStepTemplate.template_id, template.id)
                ))
              )
            ));

            return (
              <div key={key} className="workspace-inline-list__row">
                <div className="workspace-inline-list__index">
                  <label className="form-label fw-semibold">Nr.</label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    value={stepTemplate.index}
                    onChange={(event) => onUpdate(key, { index: event.target.value })}
                    required
                  />
                </div>

                <div className="workspace-inline-list__name">
                  <label className="form-label fw-semibold">Skabelon</label>
                  <select
                    className="form-select"
                    value={stepTemplate.template_id || ""}
                    onChange={(event) => {
                      const template = templates.find((option) => option.id.toString() === event.target.value.toString());
                      onUpdate(key, { template_id: event.target.value, template: template || null });
                    }}
                    required
                  >
                    <option value="">Vælg en skabelon</option>
                    {availableTemplates.map((template) => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </div>

                <div className="workspace-inline-list__action">
                  <button type="button" className="btn btn-outline-danger" onClick={() => onRemove(key)}>
                    Fjern
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function StepForm({
  data,
  setData,
  templates = [],
  errors = [],
  processing = false,
  onSubmit,
  submitLabel,
  layout = "page"
}) {
  const isDrawerLayout = layout === "drawer";
  const activeSubSteps = data.step.sub_steps_attributes.filter((subStep) => !subStep._destroy);
  const activeParticipants = data.step.step_participants_attributes.filter((participant) => !participant._destroy);
  const activeTemplates = data.step.step_templates_attributes.filter((stepTemplate) => !stepTemplate._destroy);

  const updateStep = (attributes) => {
    setData("step", { ...data.step, ...attributes });
  };

  const updateNestedItem = (collectionKey, target, attributes) => {
    updateStep({
      [collectionKey]: data.step[collectionKey].map((item) =>
        itemKey(item) === target ? { ...item, ...attributes } : item
      )
    });
  };

  const removeNestedItem = (collectionKey, target) => {
    updateStep({
      [collectionKey]: data.step[collectionKey]
        .map((item) => {
          if (itemKey(item) !== target) return item;
          if (!item.id) return null;

          return { ...item, _destroy: true };
        })
        .filter(Boolean)
    });
  };

  const addSubStep = () => {
    updateStep({
      sub_steps_attributes: [
        ...data.step.sub_steps_attributes,
        buildDraftItem(nextIndex(activeSubSteps, 1), { description: "" })
      ]
    });
  };

  const addParticipant = () => {
    updateStep({
      step_participants_attributes: [
        ...data.step.step_participants_attributes,
        buildDraftItem(nextIndex(activeParticipants))
      ]
    });
  };

  const addTemplate = () => {
    updateStep({
      step_templates_attributes: [
        ...data.step.step_templates_attributes,
        buildDraftTemplate(nextIndex(activeTemplates))
      ]
    });
  };

  return (
    <div className={isDrawerLayout ? "workspace-content workspace-content--drawer" : "workspace-content"}>
      <div className={isDrawerLayout ? "" : "container-fluid px-0"}>
        <div className={`vstack gap-4 ${isDrawerLayout ? "workspace-drawer-form-card card card-body" : "workspace-form-page"}`.trim()}>
          {errors.length > 0 ? (
            <div className="alert alert-danger mb-0">{errors.join(", ")}</div>
          ) : null}

          <form className="vstack gap-4" onSubmit={onSubmit}>
            <section className="card workspace-step-editor-card">
              <div className="card-body">
                <div className="workspace-step-editor__header">
                  <div>
                    <div className="workspace-section__eyebrow">Implementeringstrin</div>
                    <h2 className="workspace-step-editor__title">Redigér trin</h2>
                    <p className="workspace-step-editor__copy">
                      Beskriv arbejdet, rollerne og de skabeloner organisationen skal udfylde.
                    </p>
                  </div>

                  <div className="workspace-step-editor__meta">
                    <div className="workspace-step-editor__meta-card">
                      <span>Undertrin</span>
                      <strong>{activeSubSteps.length}</strong>
                    </div>
                    <div className="workspace-step-editor__meta-card">
                      <span>Skabeloner</span>
                      <strong>{activeTemplates.length}</strong>
                    </div>
                    <div className="workspace-step-editor__meta-card">
                      <span>Roller</span>
                      <strong>{activeParticipants.length}</strong>
                    </div>
                  </div>
                </div>

                <div className="row g-4 mt-1 workspace-step-fields-grid">
                  <div className="col-xl-8">
                    <label className="form-label fw-semibold" htmlFor="step-name">Navn</label>
                    <input
                      id="step-name"
                      className="form-control form-control-lg"
                      value={data.step.name}
                      onChange={(event) => updateStep({ name: event.target.value })}
                      required
                    />
                  </div>

                  <div className="col-xl-4">
                    <label className="form-label fw-semibold" htmlFor="step-index">Rækkefølge</label>
                    <input
                      id="step-index"
                      className="form-control form-control-lg"
                      type="number"
                      min="0"
                      value={data.step.index}
                      onChange={(event) => updateStep({ index: event.target.value })}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold" htmlFor="step-description">Beskrivelse</label>
                    <textarea
                      id="step-description"
                      className="form-control"
                      rows="4"
                      value={data.step.description}
                      onChange={(event) => updateStep({ description: event.target.value })}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold" htmlFor="step-completion-criteria">Kriterier for at trinnet er løst</label>
                    <textarea
                      id="step-completion-criteria"
                      className="form-control"
                      rows="4"
                      value={data.step.completion_criteria}
                      onChange={(event) => updateStep({ completion_criteria: event.target.value })}
                    />
                  </div>
                </div>
              </div>
            </section>

            <div className="row g-4">
              <div className="col-xl-6">
                <OrderedListEditor
                  eyebrow="Undertrin"
                  title="Ordnet udførelse"
                  emptyLabel="Ingen undertrin endnu."
                  addLabel="Tilføj undertrin"
                  items={activeSubSteps}
                  onAdd={addSubStep}
                  onUpdate={(target, attributes) => updateNestedItem("sub_steps_attributes", target, attributes)}
                  onRemove={(target) => removeNestedItem("sub_steps_attributes", target)}
                  nameLabel="Titel"
                  descriptionLabel="Beskrivelse"
                  minIndex={1}
                />
              </div>

              <div className="col-xl-6">
                <OrderedListEditor
                  eyebrow="Hvem bør involveres"
                  title="Roller og ansvarlige"
                  emptyLabel="Ingen involverede roller endnu."
                  addLabel="Tilføj rolle"
                  items={activeParticipants}
                  onAdd={addParticipant}
                  onUpdate={(target, attributes) => updateNestedItem("step_participants_attributes", target, attributes)}
                  onRemove={(target) => removeNestedItem("step_participants_attributes", target)}
                />
              </div>
            </div>

            <TemplateListEditor
              templates={templates}
              stepTemplates={activeTemplates}
              onAdd={addTemplate}
              onUpdate={(target, attributes) => updateNestedItem("step_templates_attributes", target, attributes)}
              onRemove={(target) => removeNestedItem("step_templates_attributes", target)}
            />

            <div className="d-flex justify-content-end">
              <button type="submit" className="btn btn-primary" disabled={processing}>{submitLabel}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
